import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function QABlock({ qaList, topicSlug, showViewAllLink = false }) {
  const [expandedId, setExpandedId] = useState(null);

  if (!qaList || qaList.length === 0) {
    return null;
  }

  return (
    <section id="qa" className="scroll-mt-24">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <HelpCircle className="w-6 h-6 text-primary" />
          Key Questions About This Topic
        </h2>
      </div>

      <div className="space-y-4">
        {qaList.map((qa) => (
          <article 
            key={qa.id}
            itemScope 
            itemProp="mainEntity" 
            itemType="https://schema.org/Question"
            className="bg-surface border border-white/10 rounded-xl overflow-hidden hover:border-primary/30 transition-all"
          >
            <button
              onClick={() => setExpandedId(expandedId === qa.id ? null : qa.id)}
              className="w-full text-left p-5 flex items-start justify-between gap-4 group"
            >
              <div className="flex-1">
                <h3 
                  itemProp="name"
                  className="font-semibold text-white text-lg group-hover:text-primary transition-colors pr-4"
                >
                  {qa.question}
                </h3>
                {expandedId !== qa.id && (
                  <p className="text-sm text-slate-500 mt-2 line-clamp-2">
                    {qa.answer.substring(0, 120)}...
                  </p>
                )}
              </div>
              <div className="flex-shrink-0 text-slate-500 group-hover:text-primary transition-colors">
                {expandedId === qa.id ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </div>
            </button>

            {expandedId === qa.id && (
              <div 
                itemScope 
                itemProp="acceptedAnswer" 
                itemType="https://schema.org/Answer"
                className="px-5 pb-5 border-t border-white/5 pt-4"
              >
                <div itemProp="text" className="text-slate-300 leading-relaxed mb-4">
                  {qa.answer}
                </div>

                {/* Attribution Links */}
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  {/* Brand Attribution */}
                  {qa.projects && (
                    <Link
                      to={`/brands/${qa.projects.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-primary transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {qa.projects.logo_url ? (
                        <img 
                          src={qa.projects.logo_url} 
                          alt={qa.projects.name} 
                          className="w-4 h-4 rounded-full object-cover"
                        />
                      ) : (
                        <span className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
                          {qa.projects.name?.charAt(0)}
                        </span>
                      )}
                      {qa.projects.name}
                    </Link>
                  )}
                  {qa.insights && (
                    <Link
                      to={`/topics/${topicSlug}/insights/${qa.insight_id}`}
                      className="flex items-center gap-1 text-primary hover:text-primary-hover transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Read full insight
                    </Link>
                  )}
                  {qa.content_episodes && (
                    <span className="text-slate-500 text-xs">
                      From: {qa.content_episodes.title || 'Episode'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </article>
        ))}
      </div>

      {showViewAllLink && qaList.length > 3 && (
        <div className="mt-6 text-center">
          <Link
            to={`/topics/${topicSlug}#qa`}
            className="inline-flex items-center gap-2 text-primary hover:text-primary-hover font-medium transition-colors"
          >
            View all questions
            <ChevronDown className="w-4 h-4" />
          </Link>
        </div>
      )}
    </section>
  );
}
